import React, { useEffect, useState } from 'react';
import SemanticExplorer from '../components/SemanticExplorer';
import './SearchResults.css';

const SearchResults = ({ searchQuery, onBackToHome, onArticleClick }) => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentSearchQuery, setCurrentSearchQuery] = useState(searchQuery);
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState(null);
    const [viewMode, setViewMode] = useState('standard'); // 'standard' or 'explorer'
    const [filters, setFilters] = useState({
        yearRange: [2010, 2024],
        publicationTypes: [],
        impactFactors: [],
        populationTypes: []
    });

    const API_KEY = '63e7d91818ff7342018bc51d165e1e25d208';
    const BASE_URL = 'https://eutils.ncbi.nlm.nih.gov/entrez/eutils/';

    // Initialize speech recognition
    useEffect(() => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            const recognition = new SpeechRecognition();
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                setCurrentSearchQuery(transcript);
                setIsListening(false);
            };

            recognition.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            setRecognition(recognition);
        }
    }, []);

    useEffect(() => {
        if (currentSearchQuery) {
            searchPubMed();
        }
    }, [currentSearchQuery, currentPage]);

    const searchPubMed = async () => {
        setLoading(true);
        try {
            // First, search for IDs
            const searchUrl = `${BASE_URL}esearch.fcgi?db=pubmed&term=${encodeURIComponent(currentSearchQuery)}&retmode=json&retmax=20&retstart=${(currentPage - 1) * 20}&api_key=${API_KEY}`;

            const searchResponse = await fetch(searchUrl);
            const searchData = await searchResponse.json();

            if (searchData.esearchresult && searchData.esearchresult.idlist) {
                const ids = searchData.esearchresult.idlist;
                setTotalResults(parseInt(searchData.esearchresult.count));

                if (ids.length > 0) {
                    // Then, get detailed information
                    const summaryUrl = `${BASE_URL}esummary.fcgi?db=pubmed&id=${ids.join(',')}&retmode=json&api_key=${API_KEY}`;
                    const summaryResponse = await fetch(summaryUrl);
                    const summaryData = await summaryResponse.json();

                    const results = ids.map(id => {
                        const article = summaryData.result[id];
                        return {
                            id: id,
                            title: article.title || 'No title available',
                            authors: article.authors ? article.authors.map(author => author.name).join(', ') : 'Unknown authors',
                            journal: article.fulljournalname || 'Unknown journal',
                            pubdate: article.pubdate || 'Unknown date',
                            abstract: article.abstract || 'No abstract available',
                            relevanceScore: Math.floor(Math.random() * 30) + 70, // Mock relevance score
                            tags: generateTags(article.title, article.abstract),
                            highlightedTerms: extractHighlightedTerms(currentSearchQuery, article.abstract)
                        };
                    });

                    setResults(results);
                } else {
                    setResults([]);
                }
            } else {
                setResults([]);
                setTotalResults(0);
            }
        } catch (error) {
            console.error('Error searching PubMed:', error);
            // Fallback to mock data
            setResults(generateMockResults());
            setTotalResults(150);
        }
        setLoading(false);
    };

    const generateMockResults = () => {
        const mockData = [
            {
                id: '1',
                title: 'Early-Life Environmental Factors and Childhood Asthma Development: A Systematic Review',
                authors: 'Johnson M, Chen L, Williams K, et al.',
                journal: 'Journal of Pediatric Pulmonology',
                pubdate: '2023',
                abstract: 'This systematic review examines the relationship between early-life environmental exposures and the development of childhood asthma. The analysis of 87 studies reveals that exposure to air pollution, tobacco smoke, and reduced microbial diversity significantly increases asthma risk in children. Conversely, early exposure to farm environments and diverse microbiota may have protective effects against asthma development. The findings suggest that environmental interventions during early childhood could significantly impact asthma prevention strategies.',
                relevanceScore: 92,
                tags: ['Systematic Review', 'Pediatric', 'Environmental Factors'],
                highlightedTerms: ['childhood asthma', 'asthma', 'children']
            },
            {
                id: '2',
                title: 'Machine Learning Approaches in Medical Diagnosis: A Comprehensive Analysis',
                authors: 'Smith A, Brown R, Davis J, et al.',
                journal: 'Artificial Intelligence in Medicine',
                pubdate: '2023',
                abstract: 'This comprehensive analysis explores the application of machine learning algorithms in medical diagnosis. The study reviews 156 clinical trials and demonstrates significant improvements in diagnostic accuracy across multiple medical specialties. Key findings include enhanced detection rates for early-stage cancers, improved cardiovascular risk assessment, and more accurate neurological disorder diagnosis.',
                relevanceScore: 88,
                tags: ['Machine Learning', 'Diagnosis', 'Clinical Trials'],
                highlightedTerms: ['medical diagnosis', 'diagnosis', 'diagnostic']
            },
            {
                id: '3',
                title: 'Novel Therapeutic Approaches for Type 2 Diabetes Management',
                authors: 'Garcia M, Rodriguez P, Martinez L, et al.',
                journal: 'Diabetes Research and Clinical Practice',
                pubdate: '2023',
                abstract: 'This study investigates innovative therapeutic strategies for type 2 diabetes management, focusing on personalized medicine approaches. The research examines the efficacy of combination therapies, including novel drug formulations and lifestyle intervention programs. Results indicate significant improvements in glycemic control and reduced cardiovascular complications.',
                relevanceScore: 85,
                tags: ['Type 2 Diabetes', 'Therapeutics', 'Personalized Medicine'],
                highlightedTerms: ['type 2 diabetes', 'diabetes', 'diabetic']
            }
        ];
        return mockData;
    };

    const generateTags = (title, abstract) => {
        const commonTags = ['Systematic Review', 'Clinical Trial', 'Meta-Analysis', 'Case Study', 'Review'];
        const medicalTags = ['Pediatric', 'Cardiology', 'Oncology', 'Neurology', 'Endocrinology'];
        const methodTags = ['Machine Learning', 'Environmental Factors', 'Therapeutics', 'Diagnosis', 'Prevention'];

        const allTags = [...commonTags, ...medicalTags, ...methodTags];
        const selectedTags = [];

        // Select 2-3 random tags
        for (let i = 0; i < Math.floor(Math.random() * 2) + 2; i++) {
            const randomTag = allTags[Math.floor(Math.random() * allTags.length)];
            if (!selectedTags.includes(randomTag)) {
                selectedTags.push(randomTag);
            }
        }

        return selectedTags;
    };

    const extractHighlightedTerms = (query, abstract) => {
        if (!abstract || !query) return [];

        const queryTerms = query.toLowerCase().split(' ').filter(term => term.length > 3);
        const highlightedTerms = [];

        queryTerms.forEach(term => {
            if (abstract.toLowerCase().includes(term)) {
                highlightedTerms.push(term);
            }
        });

        // Add some common medical terms that might be in the abstract
        const commonTerms = ['treatment', 'therapy', 'diagnosis', 'patient', 'clinical', 'study', 'research'];
        commonTerms.forEach(term => {
            if (abstract.toLowerCase().includes(term) && !highlightedTerms.includes(term)) {
                highlightedTerms.push(term);
            }
        });

        return highlightedTerms.slice(0, 3); // Limit to 3 terms
    };

    const handleSearch = () => {
        if (currentSearchQuery.trim()) {
            setCurrentPage(1);
            searchPubMed();
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleViewModeToggle = () => {
        setViewMode(prev => {
            const newMode = prev === 'standard' ? 'explorer' : 'standard';
            return newMode;
        });
    };

    const handleBackToStandard = () => {
        setViewMode('standard');
    };

    const handleVoiceSearch = () => {
        if (isListening) {
            recognition.stop();
        } else {
            recognition.start();
            setIsListening(true);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleArticleClick = (result) => {
        // Create article object with all necessary fields for ArticleDetails
        const article = {
            id: result.id,
            title: result.title,
            authors: result.authors,
            journal: result.journal,
            date: result.pubdate,
            abstract: result.abstract,
            volume: "42",
            issue: "5",
            pages: "1234-1245",
            type: "Research Article",
            citations: "45",
            impactFactor: "38.5 (2023)",
            accessType: "Open Access",
            doi: `10.1234/${result.id}.2023.001`,
            pmid: result.id
        };

        onArticleClick(article);
    };

    const totalPages = Math.ceil(totalResults / 20);

    // Render explorer view if selected
    if (viewMode === 'explorer') {
        return <SemanticExplorer searchQuery={currentSearchQuery} onBackToStandard={handleBackToStandard} />;
    }

    return (
        <div className="search-results-page">
            {/* Logo Section Above Navbar */}
            <div className="logo-section">
                <div className="logo-container">
                    <div className="logo">
                        <i className="fas fa-search"></i>
                        <span>MedSearch</span>
                    </div>
                </div>
            </div>

            {/* Header */}
            <header className="results-header">
                <div className="header-content">
                    <div className="header-left">
                        <button className="back-btn" onClick={onBackToHome}>
                            <i className="fas fa-arrow-left"></i>
                            Back
                        </button>
                    </div>
                    <div className="search-bar">
                        <input
                            type="text"
                            value={currentSearchQuery}
                            onChange={(e) => setCurrentSearchQuery(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Search medical literature..."
                        />
                        <button
                            className={`voice-search ${isListening ? 'listening' : ''}`}
                            onClick={handleVoiceSearch}
                            title={isListening ? 'Stop listening' : 'Start voice search'}
                        >
                            <i className={`fas ${isListening ? 'fa-stop' : 'fa-microphone'}`}></i>
                        </button>
                        <button className="search-button" onClick={handleSearch}>
                            <i className="fas fa-search"></i> Search
                        </button>
                    </div>
                    <div className="view-toggle-container">
                        <div className="view-toggle">
                            <span className={`toggle-label ${viewMode === 'standard' ? 'active' : ''}`}>Standard View</span>
                            <div className={`toggle-switch ${viewMode === 'explorer' ? 'active' : ''}`} onClick={handleViewModeToggle}>
                                <div className="toggle-slider"></div>
                            </div>
                            <span className={`toggle-label ${viewMode === 'explorer' ? 'active' : ''}`}>Explorer View</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Search Info */}
            <div className="search-info">
                <div className="search-info-content">
                    <div className="search-query">
                        Showing results for: <strong>"{currentSearchQuery}"</strong>
                    </div>
                    <div className="semantic-expansion">
                        <span>Semantic expansion:</span>
                        <span className="expansion-tag">medical research</span>
                        <span className="expansion-tag">clinical studies</span>
                        <span className="expansion-tag">healthcare</span>
                    </div>
                </div>
            </div>

            {/* Results Container */}
            <div className="results-container">
                {/* Main Results Section */}
                <div className="results-main">
                    <div className="results-header-section">
                        <div className="results-count">
                            {loading ? 'Searching...' : `${totalResults.toLocaleString()} results found`}
                        </div>
                        <div className="sort-options">
                            <label>Sort by:</label>
                            <select defaultValue="relevance">
                                <option value="relevance">Relevance</option>
                                <option value="date">Date</option>
                                <option value="citations">Citations</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading">Searching PubMed database...</div>
                    ) : (
                        <div className="results-list">
                            {results.map((result) => (
                                <div
                                    key={result.id}
                                    className="result-card"
                                    onClick={() => handleArticleClick(result)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="result-content">
                                        <h3 className="result-title">{result.title}</h3>
                                        <div className="result-metadata">
                                            <span className="result-authors">{result.authors}</span>
                                            <span className="result-journal">
                                                <i className="fas fa-file-alt"></i> {result.journal}
                                            </span>
                                            <span className="result-year">
                                                <i className="fas fa-calendar"></i> {result.pubdate}
                                            </span>
                                        </div>
                                        <p className="result-abstract">
                                            {result.abstract.split(' ').map((word, index) => {
                                                const lowerWord = word.toLowerCase().replace(/[.,;!?]/g, '');
                                                const isHighlighted = result.highlightedTerms.some(term =>
                                                    lowerWord.includes(term.toLowerCase())
                                                );
                                                return isHighlighted ? (
                                                    <span key={index} className="highlighted">{word} </span>
                                                ) : (
                                                    <span key={index}>{word} </span>
                                                );
                                            })}
                                        </p>
                                        <div className="result-tags">
                                            {result.tags.map((tag, index) => (
                                                <span key={index} className="tag">{tag}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div
                                        className="relevance-score"
                                        style={{ '--percentage': result.relevanceScore }}
                                        data-score={result.relevanceScore}
                                    >
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const page = i + 1;
                                return (
                                    <button
                                        key={page}
                                        className={`page-btn ${currentPage === page ? 'active' : ''}`}
                                        onClick={() => handlePageChange(page)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </div>

                {/* Refine Sidebar */}
                <div className="refine-sidebar">
                    <h3>Refine Results</h3>

                    <div className="filter-section">
                        <h4>Publication Year</h4>
                        <div className="year-slider">
                            <input
                                type="range"
                                min="1990"
                                max="2024"
                                value={filters.yearRange[1]}
                                onChange={(e) => setFilters({
                                    ...filters,
                                    yearRange: [filters.yearRange[0], parseInt(e.target.value)]
                                })}
                            />
                            <div className="year-range">
                                {filters.yearRange[0]} - {filters.yearRange[1]}
                            </div>
                        </div>
                    </div>

                    <div className="filter-section">
                        <h4>Publication Type</h4>
                        <div className="checkbox-list">
                            <label className="checkbox-item">
                                <input type="checkbox" /> Systematic Review
                            </label>
                            <label className="checkbox-item">
                                <input type="checkbox" /> Clinical Trial
                            </label>
                            <label className="checkbox-item">
                                <input type="checkbox" /> Meta-Analysis
                            </label>
                            <label className="checkbox-item">
                                <input type="checkbox" /> Case Study
                            </label>
                        </div>
                    </div>

                    <div className="filter-section">
                        <h4>Impact Factor</h4>
                        <div className="impact-chart">
                            <div className="impact-bar">
                                <span className="range-label">0-2</span>
                                <div className="bar" style={{ width: '60%' }}></div>
                            </div>
                            <div className="impact-bar">
                                <span className="range-label">2-5</span>
                                <div className="bar" style={{ width: '80%' }}></div>
                            </div>
                            <div className="impact-bar">
                                <span className="range-label">5+</span>
                                <div className="bar" style={{ width: '40%' }}></div>
                            </div>
                        </div>
                    </div>

                    <div className="filter-section">
                        <h4>Population Type</h4>
                        <div className="population-grid">
                            <button className="population-btn">
                                <i className="fas fa-baby"></i>
                                <span>Pediatric</span>
                            </button>
                            <button className="population-btn">
                                <i className="fas fa-user"></i>
                                <span>Adult</span>
                            </button>
                            <button className="population-btn">
                                <i className="fas fa-users"></i>
                                <span>General</span>
                            </button>
                            <button className="population-btn">
                                <i className="fas fa-user-md"></i>
                                <span>Clinical</span>
                            </button>
                        </div>
                    </div>

                    <button className="apply-filters-btn">
                        Apply Filters
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SearchResults;